import { formatValue } from './common';
import { formatFieldEdition } from './summary';

import type { ChangelogDiff, ChangelogSummaryRule } from './types';

export const SCREENS_ORDER = 2000;

const EDITABLE_FIELDS = ['audienceRadius', 'layerId'];

/** New format is keyed by deviceId (string); old format is an array (numeric index) */
function getDeviceId(change: ChangelogDiff): string | undefined {
  const key = change.path?.[0];
  return typeof key === 'string' ? key : undefined;
}

function getScreenDeviceId(screen: unknown, fallback?: string): string {
  const deviceId = screen && typeof screen === 'object' && 'deviceId' in screen ? screen.deviceId : undefined;
  return formatValue(deviceId ?? fallback);
}

function withDeviceId(change: ChangelogDiff, label: string, separator: string): string {
  const deviceId = getDeviceId(change);
  return deviceId ? `${deviceId}${separator}${label}` : label;
}

export const screensRules: ChangelogSummaryRule[] = [
  {
    // Old format: screen added or removed from the array
    format: change => {
      if (change.item?.kind === 'N') {
        return { category: 'added', label: getScreenDeviceId(change.item.rhs) };
      }
      if (change.item?.kind === 'D') {
        return { category: 'deleted', label: getScreenDeviceId(change.item.lhs) };
      }
      return undefined;
    },
    order: SCREENS_ORDER,
    regex: /^A$/,
    section: 'screens'
  },
  {
    // New format: screen added or removed from the deviceId map
    format: change => {
      const deviceId = getDeviceId(change);
      if (!deviceId || change.path?.length !== 1) {
        return undefined;
      }
      return change.kind === 'N'
        ? { category: 'added', label: getScreenDeviceId(change.rhs, deviceId) }
        : { category: 'deleted', label: getScreenDeviceId(change.lhs, deviceId) };
    },
    order: SCREENS_ORDER,
    regex: /^[ND]\./,
    section: 'screens'
  },
  {
    // Both formats: group added or removed from a screen
    format: change => {
      if (change.item?.kind !== 'N' && change.item?.kind !== 'D') {
        return undefined;
      }
      const added = change.item.kind === 'N';
      return {
        category: added ? 'groupsAdded' : 'groupsDeleted',
        label: withDeviceId(change, formatValue(added ? change.item.rhs : change.item.lhs), ': ')
      };
    },
    order: SCREENS_ORDER,
    regex: /^A\..+\.groups$/,
    section: 'screens'
  },
  {
    // Both formats: group replaced inside the groups array
    format: (change, context) => ({
      category: 'updated',
      label: withDeviceId(
        change,
        formatFieldEdition(
          context.formatMessage({ defaultMessage: 'group', id: 'changelogModal.message.group' }),
          change,
          context
        ),
        ' '
      )
    }),
    order: SCREENS_ORDER,
    regex: /^E\..+\.groups(\.\d+)?$/,
    section: 'screens'
  },
  {
    // New format: deleted flag toggled
    format: change => {
      const deviceId = getDeviceId(change);
      if (!deviceId || change.path?.length !== 2) {
        return undefined;
      }
      return { category: change.rhs ? 'deleted' : 'restored', label: deviceId };
    },
    order: SCREENS_ORDER,
    regex: /^E\..+\.deleted$/,
    section: 'screens'
  },
  {
    // New format: screen field edition
    format: (change, context) => {
      const deviceId = getDeviceId(change);
      const field = change.path?.[1];
      if (!deviceId || change.path?.length !== 2 || typeof field !== 'string' || !EDITABLE_FIELDS.includes(field)) {
        return undefined;
      }
      return { category: 'updated', label: `${deviceId} ${formatFieldEdition(field, change, context)}` };
    },
    order: SCREENS_ORDER,
    regex: /^E\./,
    section: 'screens'
  }
];
